import React, { useState } from "react";
import { useUser } from "../../../hooks/user";
import {
  REALTOR_FIELDS,
  removeRealtorAt,
  searchRealtors,
  sortRealtors,
  updateRealtorAt,
  realtorCsvTemplate,
  realtorDirectoryToCsv,
  realtorsFromCsv,
  mergeRealtors
} from "../../../constants/realtor_directory";
import Image from "../../ui/image";
import "./css/full_realtor_list.scss";

// Full Realtor List (Purchase only). The company-wide Realtor Directory:
// search by first name / last name / company, edit an agent when they change firms,
// and print the list. Lives in the Forms menu (not the Admin Panel) because the Admin
// Panel is gated to company_admin_role — Loan Officers would be locked out of it.
//
// Editing here affects FUTURE picks only: loan files that already captured an agent
// keep the snapshot they saved.
export default function FullRealtorList() {
  const [
    { companyProfile, companyProfileError },
    { saveCompanyProfile }
  ] = useUser();
  const [query, setQuery] = useState("");
  const [editKey, setEditKey] = useState(null); // index in the unsorted directory
  const [draft, setDraft] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [uploadKey, setUploadKey] = useState(Date.now()); // reset the file input
  const fileInputRef = React.useRef(null);

  const directory = (companyProfile && companyProfile.realtor_directory) || [];
  const visible = sortRealtors(searchRealtors(directory, query));

  function downloadTemplate() {
    const blob = new Blob([realtorCsvTemplate()], {
      type: "text/csv;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Realtor List Template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Export the company's current Realtor Directory as a CSV (same columns as the
  // upload), so the list can be saved off or re-imported elsewhere.
  function exportList() {
    const blob = new Blob([realtorDirectoryToCsv(directory)], {
      type: "text/csv;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const company =
      ((companyProfile && companyProfile.company_name) || "Company")
        .replace(/[^\w\s-]/g, "")
        .trim() || "Company";
    a.download = `${company} Realtor List.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleUpload(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const incoming = realtorsFromCsv(e.target.result);
      if (incoming.length === 0) {
        setUploadSummary({
          error:
            "Couldn't read any realtors. Make sure the file has the header row: Name, Company, Office Ph, Cell, Email, License #."
        });
        setUploadKey(Date.now());
        return;
      }
      const result = mergeRealtors(directory, incoming);
      if (result.added > 0 || result.updated > 0) {
        persist(result.directory);
      }
      setUploadSummary(result);
      setShowDetails(false);
      setUploadKey(Date.now());
    };
    reader.readAsText(file);
  }

  function persist(nextDirectory) {
    saveCompanyProfile({ ...companyProfile, realtor_directory: nextDirectory });
  }

  function startEdit(realtor) {
    setEditKey(directory.indexOf(realtor));
    setDraft({ ...realtor });
  }

  function saveEdit() {
    if (editKey == null || editKey < 0) return;
    persist(updateRealtorAt(directory, editKey, draft));
    setEditKey(null);
    setDraft(null);
  }

  function deleteRealtor(realtor) {
    const index = directory.indexOf(realtor);
    if (index < 0) return;
    if (
      !window.confirm(
        `Remove ${realtor.name || "this realtor"} from the Realtor List? Loan files that already used them keep their information.`
      )
    ) {
      return;
    }
    persist(removeRealtorAt(directory, index));
  }

  const today = new Date();
  const dateStr = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;

  return (
    <div className="frl-doc">
      <div className="frl-header">
        <div className="frl-logo">
          <Image url="company/company-logo" className="frl-logo-img" />
        </div>
        <div className="frl-title-block">
          <div className="frl-title">Full Realtor List</div>
          <div className="frl-sub">
            {(companyProfile && companyProfile.company_name) || ""}
          </div>
          <div className="frl-sub">
            {directory.length} realtor{directory.length === 1 ? "" : "s"} · printed{" "}
            {dateStr}
          </div>
        </div>
      </div>

      <div className="frl-search no-print">
        <input
          className="frl-search-input"
          type="text"
          value={query}
          placeholder="Search by first name, last name, or company…"
          onChange={e => setQuery(e.target.value)}
        />
        {query && (
          <button className="button" onClick={() => setQuery("")}>
            Clear
          </button>
        )}
        <button className="button" onClick={() => window.print()}>
          Print
        </button>
        <button className="button" onClick={downloadTemplate}>
          Download Template
        </button>
        {directory.length > 0 && (
          <button className="button" onClick={exportList}>
            Export My List
          </button>
        )}
        <button
          className="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          Upload CSV
        </button>
        <input
          key={uploadKey}
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={handleUpload}
        />
      </div>

      <div className="frl-upload-note no-print">
        Uploading updates your company Realtor List. If a realtor&rsquo;s License #
        (or email) already exists, their information is updated — so a list with new
        company names refreshes the directory. Realtors already saved on existing loan
        files keep the information they saved.
      </div>

      {uploadSummary && (
        <div className="frl-upload-summary no-print">
          {uploadSummary.error ? (
            <div className="frl-error">{uploadSummary.error}</div>
          ) : (
            <>
              <div className="frl-upload-counts">
                <b>{uploadSummary.added}</b> added ·{" "}
                <b>{uploadSummary.updated}</b> updated ·{" "}
                <b>{uploadSummary.skipped}</b> unchanged
                {uploadSummary.details.length > 0 && (
                  <button
                    className="frl-link"
                    onClick={() => setShowDetails(d => !d)}
                  >
                    {showDetails ? "Hide details" : "Show details"}
                  </button>
                )}
                <button
                  className="frl-link"
                  onClick={() => setUploadSummary(null)}
                >
                  Dismiss
                </button>
              </div>
              {showDetails && (
                <ul className="frl-upload-details">
                  {uploadSummary.details.map((d, i) => (
                    <li key={i}>
                      {d.type === "added" ? "Added" : "Updated"}: {d.name}
                      {d.firm ? ` — ${d.firm}` : ""}
                      {d.type === "updated" && d.changes
                        ? ` (${d.changes
                            .map(c => (c === "firm" ? "company" : c.replace("_", " ")))
                            .join(", ")})`
                        : ""}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {companyProfileError && (
        <div className="frl-error no-print">
          Realtor List not saved: {companyProfileError}
        </div>
      )}

      <table className="frl-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Office Ph</th>
            <th>Cell</th>
            <th>Email</th>
            <th>License #</th>
            <th className="no-print" />
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan="7" className="frl-empty">
                {directory.length === 0
                  ? "No realtors yet. They are added automatically when you save the Realtor Agent Information form."
                  : "No realtors match that search."}
              </td>
            </tr>
          )}
          {visible.map((r, i) => (
            <tr key={i}>
              <td>{r.name}</td>
              <td>{r.firm}</td>
              <td>{r.office_phone}</td>
              <td>{r.cell_phone}</td>
              <td>{r.email}</td>
              <td>{r.license}</td>
              <td className="no-print frl-row-actions">
                <button className="button" onClick={() => startEdit(r)}>
                  Edit
                </button>
                <button className="button" onClick={() => deleteRealtor(r)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {draft && (
        <div className="frl-edit no-print">
          <div className="frl-edit-title">Edit Realtor</div>
          <div className="frl-edit-fields">
            {REALTOR_FIELDS.map(f => (
              <div className="frl-edit-field" key={f}>
                <div className="frl-edit-label">
                  {f === "firm"
                    ? "Company"
                    : f === "office_phone"
                      ? "Office Ph"
                      : f === "cell_phone"
                        ? "Cell Ph"
                        : f === "license"
                          ? "License #"
                          : f.charAt(0).toUpperCase() + f.slice(1)}
                </div>
                <input
                  className="frl-edit-input"
                  type="text"
                  value={draft[f] || ""}
                  onChange={e => setDraft({ ...draft, [f]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="frl-edit-note">
            Changes apply to future picks. Loan files that already used this realtor
            keep the information they saved.
          </div>
          <div className="frl-edit-actions">
            <button className="button" onClick={saveEdit}>
              Save
            </button>
            <button
              className="button"
              onClick={() => {
                setEditKey(null);
                setDraft(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
